import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/modules/commonComponents/dialog/DialogPrimitives';
import {
  dialogBodyScrollClassName,
  dialogShellGridClassName,
  dialogViewportMaxHeightClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';

const AppDialog = ({
  open,
  onOpenChange,
  title,
  titleSrOnly = false,
  description,
  headerEnd = null,
  children,
  footer,
  maxWidthClassName = 'sm:max-w-lg',
  contentClassName = null,
  bodyClassName = null,
  headerVariant = 'muted',
  showCloseButton = true,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  onConfirm,
  onCancel,
  confirmText = '確認',
  cancelText = '取消',
  cancelAriaLabel = null,
  showCancel = true,
  confirmDisabled = false,
}) => {
  const hasDefaultFooter = Boolean(onConfirm || onCancel);
  const hasBody = Boolean(children);
  const resolvedFooter = footer ?? (hasDefaultFooter ? (
    <DialogFooter>
      {showCancel && onCancel ? (
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          aria-label={cancelAriaLabel || cancelText}
        >
          {cancelText}
        </Button>
      ) : null}
      {onConfirm ? (
        <Button type="button" onClick={onConfirm} disabled={confirmDisabled}>
          {confirmText}
        </Button>
      ) : null}
    </DialogFooter>
  ) : null);

  const titleBlock = (
    <>
      <DialogTitle className={cn(titleSrOnly && 'sr-only')}>
        {title}
      </DialogTitle>
      {description ? (
        <DialogDescription>{description}</DialogDescription>
      ) : null}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          maxWidthClassName,
          dialogViewportMaxHeightClassName,
          hasBody && dialogShellGridClassName,
          contentClassName,
          !hasBody && 'gap-0',
        )}
        showCloseButton={showCloseButton}
        headerVariant={headerVariant}
        closeOnOutsideClick={closeOnOutsideClick}
        closeOnEscape={closeOnEscape}
      >
        <DialogHeader variant={headerVariant}>
          {headerEnd ? (
            <div
              className={cn(
                'flex flex-col gap-2',
                'sm:flex-row sm:items-center sm:justify-between sm:gap-3',
                showCloseButton && 'pr-12',
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                {titleBlock}
              </div>
              <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
                {headerEnd}
              </div>
            </div>
          ) : (
            titleBlock
          )}
        </DialogHeader>
        {hasBody ? (
          <div className={cn(dialogBodyScrollClassName, bodyClassName)}>
            {children}
          </div>
        ) : null}
        {resolvedFooter}
      </DialogContent>
    </Dialog>
  );
};

AppDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  titleSrOnly: PropTypes.bool,
  description: PropTypes.node,
  headerEnd: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  maxWidthClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  headerVariant: PropTypes.oneOf(['default', 'muted', 'primary']),
  showCloseButton: PropTypes.bool,
  closeOnOutsideClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  cancelAriaLabel: PropTypes.string,
  showCancel: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
};

export default AppDialog;
